import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField,
} from "@mui/material";

import { fetchWithAuth } from "utils/fetchWithAuth";

function RevisionLoyerModal({
  open,
  onClose,
  location,
  lot,
  revision,
  onValidated
}) {

  const [revisionCharges, setRevisionCharges] = useState("");
  const [revisionPdfUrl, setRevisionPdfUrl] = useState(null);
  const [loadingRevisionPreview, setLoadingRevisionPreview] = useState(false);
  const [validatingRevision, setValidatingRevision] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = (severity, text) => {
    setMessage({severity, text});
  };

  const isMeuble = Boolean(
    lot?.bien?.meuble ?? location?.lot?.bien?.meuble
  );

  const locataire =
    location?.locataire ||
    location?.locataire_referent ||
    location?.locationlocataire ||
    {};

  const nomLocataire = [
    locataire.prenom,
    locataire.nom,
  ]
    .filter(Boolean)
    .join(" ");

  const titre = nomLocataire
    ? `Révision du loyer de ${nomLocataire}`
    : "Révision du loyer";

  const revokePdfUrl = () => {
    setRevisionPdfUrl((oldUrl) => {
      if (oldUrl) {
        window.URL.revokeObjectURL(oldUrl);
      }

      return null;
    });
  };

  useEffect(() => {
    if (!open) return;

    setMessage(null);
    setRevisionCharges(
      isMeuble ? "" : String(location?.charges ?? 0)
    );

    revokePdfUrl();
  }, [open, location?.id, location?.charges, isMeuble]);

  useEffect(() => {
    return () => {
      if (revisionPdfUrl) {
        window.URL.revokeObjectURL(revisionPdfUrl);
      }
    };
  }, [revisionPdfUrl]);

  const getChargesAEnvoyer = () => {
    if (isMeuble) {
      return 0;
    }

    if (revisionCharges === "") {
      return Number(location?.charges ?? 0);
    }

    return Number(revisionCharges);
  };

  const validateCharges = () => {
    if (isMeuble) return;

    const charges = getChargesAEnvoyer();

    if (Number.isNaN(charges)) {
      throw new Error(
        "Veuillez saisir une provision de charges valide"
      );
    }

    if (charges < 0) {
      throw new Error(
        "La provision de charges ne peut pas être négative"
      );
    }
  };

  const generateRevisionPreview = async () => {
    if (!location?.id || loadingRevisionPreview) return;

    setLoadingRevisionPreview(true);

    try {
      validateCharges();

      const API_URL = process.env.REACT_APP_API_URL;
      const chargesAEnvoyer = getChargesAEnvoyer();

      const res = await fetchWithAuth(
        `${API_URL}/api/revision/${location.id}/courrier/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "preview",
            revision,
            charges_revisees: chargesAEnvoyer,
          }),
        }
      );

      if (!res.ok) {
        let message = "Erreur lors de la génération de la preview";

        try {
          const error = await res.json();
          message = error.detail || message;
        } catch {
          // Réponse non JSON
        }

        throw new Error(message);
      }

      const blob = await res.blob();
      const newUrl = window.URL.createObjectURL(blob);

      setRevisionPdfUrl((oldUrl) => {
        if (oldUrl) {
          window.URL.revokeObjectURL(oldUrl);
        }

        return newUrl;
      });
    } catch (error) {
        showMessage("error", error.message);
    } finally {
        setLoadingRevisionPreview(false);
    }
  };

  const handleValidateRevision = async () => {
    if (!location?.id || validatingRevision) return;

    setValidatingRevision(true);

    try {
      validateCharges();

      const API_URL = process.env.REACT_APP_API_URL;
      const chargesAEnvoyer = getChargesAEnvoyer();

      const res = await fetchWithAuth(
        `${API_URL}/api/revision/${location.id}/courrier/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "valider",
            revision,
            charges_revisees: chargesAEnvoyer,
          }),
        }
      );

      if (!res.ok) {
        let message = "Erreur lors de la validation de la révision";

        try {
          const error = await res.json();
          message = error.detail || message;
        } catch {
          // Réponse non JSON
        }

        throw new Error(message);
      }

      revokePdfUrl();

      showMessage("success", "Révision validée et courrier enregistré");

      if (onValidated) {
        await onValidated();
      }

      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (error) {
        showMessage("error", error.message);
    } finally {
        setValidatingRevision(false);
    }
  };

  const handleClose = () => {
    if (validatingRevision) return;

    revokePdfUrl();
    onClose();
  };

  const chargesInvalides =
    !isMeuble &&
    (
      revisionCharges === "" ||
      Number.isNaN(Number(revisionCharges)) ||
      Number(revisionCharges) < 0
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontSize: "1.25rem", fontWeight: 500 }}>
        {titre}
      </DialogTitle>

      <DialogContent>
        {message && (
            <Alert
                severity={message.severity}
                sx={{ mb: 2 }}
                onClose={() => setMessage(null)}
            >
                {message.text}
            </Alert>
        )}
        {!isMeuble && (
          <TextField
            fullWidth
            margin="normal"
            label="Provision pour charges"
            type="number"
            value={revisionCharges}
            onChange={(event) => {
              setRevisionCharges(event.target.value);
              revokePdfUrl();
            }}
            inputProps={{
              min: 0,
              step: "0.01",
            }}
            helperText={`Valeur actuelle : ${
              location?.charges ?? 0
            } €`}
          />
        )}

        {isMeuble && (
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Ce logement est meublé, la provision de charges n'est pas modifiable
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={generateRevisionPreview}
            disabled={
              loadingRevisionPreview ||
              validatingRevision ||
              chargesInvalides
            }
          >
            {loadingRevisionPreview
              ? "Génération..."
              : "Générer la preview"}
          </Button>
        </Box>

        {loadingRevisionPreview && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {revisionPdfUrl && !loadingRevisionPreview && (
          <Box sx={{ mt: 2 }}>
            <iframe
              src={revisionPdfUrl}
              width="100%"
              height="400"
              title="Prévisualisation PDF"
              style={{
                border: "1px solid #ddd",
                borderRadius: 4,
                display: "block",
              }}
            />

            <Button
              component="a"
              href={revisionPdfUrl}
              download="revision_loyer.pdf"
              color="success"
              sx={{ mt: 2 }}
            >
              Télécharger le PDF
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={validatingRevision}
        >
          Annuler
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleValidateRevision}
          disabled={
            !revisionPdfUrl ||
            validatingRevision ||
            loadingRevisionPreview
          }
        >
          {validatingRevision ? "Validation..." : "Suivant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RevisionLoyerModal;